import { createContext, use, useContext } from "react";
import { useLoaderData as useLoaderDataBase } from "react-router-dom";
import * as Sentry from "@sentry/react";
import {
  App,
  AuthContextSchema,
  AuthContext as BaseAuthContextType,
  ForwardingParams,
  ForwardingParamsSchema,
  routes as allRoutes,
  fullEndpoints,
  hosts,
  isNullOrEmptyString,
} from "@songtradr-sso/shared/common";
import { Api } from "../shared/utils/api.utils";

// TEXT

// STYLES

// DATA
export const getAuthContext = async (params: ForwardingParams) => {
  const result = await Api.request({
    method: "POST",
    url: fullEndpoints[App.Core].auth.context,
    data: {
      ...params,
      workspace: getWorkspaceFromSubdomain(),
    },
  }).then((res) => AuthContextSchema.parse(res.data.data));

  Sentry.setTag("src", result.appId);
  Sentry.setTag("org_name", result.orgName);

  return result;
};

// TYPES
export type LoaderContext = {
  auth?: ReturnType<typeof getAuthContext>;
};

export type LoaderData = {
  getAuthContext: ReturnType<typeof getAuthContext>;
};

// CONSTANTS
export const routes = allRoutes[App.Core];
export const APP_LAYOUT_ID = "app-layout";

export const AuthContext = createContext<BaseAuthContextType>({});

// FUNCTIONS
export const shouldRedirectToSignIn = (url: URL) => {
  if (url.pathname !== routes.base) return false;

  const isWorkspaceDomain = url.origin !== hosts.frontend[App.Core];
  const hasNoWorkspace = url.searchParams.get("no_ws") === "true";

  return isWorkspaceDomain || hasNoWorkspace;
};

export const useLoaderData = () => {
  const { getAuthContext } = useLoaderDataBase<LoaderData>();
  return { getAuthContext: use(getAuthContext) };
};

export const useAuthContext = () => useContext(AuthContext);

const getWorkspaceFromSubdomain = (): string | undefined => {
  // Handle preview domains differently...
  if (hosts.environment === "preview") return getWorkspaceForPrPreview();

  const hostname = window.location.hostname;
  const parts = hostname.split(".");
  const coreUrl = new URL(hosts.frontend[App.Core]);
  const baseDomain = coreUrl.hostname;

  if (
    hostname.endsWith(`.${baseDomain}`) &&
    parts.length > baseDomain.split(".").length
  ) {
    return parts[0];
  }

  return undefined;
};

const getWorkspaceForPrPreview = (): string | undefined => {
  const hostname = window.location.hostname;
  // For PR previews, the host is pr-{id}.preview.auth.songtradr.dev
  // We need to extract just auth.songtradr.dev as the base domain
  const prSuffix = `.preview.auth.songtradr.dev`;
  if (!hostname.endsWith(prSuffix)) return undefined;

  // Extract the first label, i.e. loreal-pr-1126.preview.auth.songtradr.dev
  const labels = hostname.slice(0, -prSuffix.length).split(".").filter(Boolean);
  if (labels.length === 0) return undefined;

  const firstLabel = labels[0];

  // Match <workspace>-pr-<number>
  const match = firstLabel.match(/^([a-z0-9-]+)-pr-\d+$/i);
  if (match) return match[1].toLowerCase();

  // If pure pr-<number> (no workspace), or other pattern → undefined
  return undefined;
};

export const getForwardingParams = (urlParams: URLSearchParams) => {
  // Convert the URLSearchParams to an object & filter out desired params...
  const { src, follow } = Object.fromEntries(urlParams.entries());

  // If src exists, validate & return forwarding params...
  const forwarding = !isNullOrEmptyString(src)
    ? ForwardingParamsSchema.parse({ src, follow })
    : ({} as ForwardingParams);

  return forwarding;
};

// ASSETS
